import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import Modal from './Modal';

function FirmenList() {
    const [firmen, setFirmen] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [password, setPassword] = useState('');
    const [selectedFirma, setSelectedFirma] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadingFirmen, setLoadingFirmen] = useState(true);
    const navigate = useNavigate();
      

    // Firmen laden
    const fetchFirmen = async () => {
        try {
            setLoadingFirmen(true);
            const response = await fetch('https://company-application-platform-backend.onrender.com/api/firma', {
                method: 'GET',
                headers: {
                    'X-API-Key': process.env.REACT_APP_API_KEY,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setFirmen(data);
            } else {
                console.error('Fehler beim Abrufen der Firmen');
            }
        } catch (error) {
            console.error('Fehler beim Abrufen der Firmen:', error);
        } finally {
            setLoadingFirmen(false);
        }
    };

    useEffect(() => {
        fetchFirmen();
        const handleStorageChange = (e) => {
            if (e.key === 'firmaStatusUpdated') {
                fetchFirmen();
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // Modal öffnen
    const handleEditClick = (firma) => {
        setSelectedFirma(firma);
        setPassword('');
        setIsModalOpen(true);
    };

    // Firma bearbeiten
    const handleUpdateFirma = async () => {
        if (!selectedFirma || !selectedFirma.id) return;
        
        const sanitizedPassword = DOMPurify.sanitize(password.trim());
        if (!sanitizedPassword || sanitizedPassword.length < 5) return;

        setLoading(true);
        try {
            navigate(`/edit-firma/${selectedFirma.id}`, { 
                state: { 
                    password: sanitizedPassword,
                    firma: selectedFirma 
                } 
            });
            handleModalClose();
        } catch (error) {
            console.error('Fehler beim Weiterleiten:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setPassword('');
        setSelectedFirma(null);
        setLoading(false);
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'offen': return 'status-badge status-offen';
            case 'in Arbeit': return 'status-badge status-in-arbeit';
            case 'versendet': return 'status-badge status-versendet';
            case 'zusage': return 'status-badge bg-green-100 text-green-800';
            case 'absage': return 'status-badge bg-red-100 text-red-800';
            default: return 'status-badge bg-gray-100 text-gray-800';
        }
    };

    const filteredFirmen = firmen.filter(firma =>
        (firma.firmenname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.kontaktperson || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.adresse || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (firma.telefon || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            <section className="hero-section py-12">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-3xl font-bold text-gray-700">Firmenübersicht</h1>
                </div>
            </section>

            <section className="page-content-section py-8">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        {/* Suchleiste */}
                        <div className="search-section">
                            <div className="search-header">
                                <div className="search-info">
                                    <h2 className="search-title">
                                        Registrierte Firmen
                                    </h2>
                                    <p className="search-subtitle">
                                        {loadingFirmen ? 'Lade...' : `${firmen.length} Firmen gefunden`}
                                        {searchTerm && ` (${filteredFirmen.length} gefiltert)`}
                                    </p>
                                </div>
                                <div className="search-input-container">
                                    <div className="search-input-wrapper">
                                        <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                                        </svg>
                                        <input
                                            type="text"
                                            placeholder="Firma, Kontakt oder E-Mail suchen..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="search-input"
                                        />
                                        {searchTerm && (
                                            <button
                                                onClick={() => setSearchTerm('')}
                                                className="search-clear-btn"
                                                type="button"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Firmenliste */}
                        {loadingFirmen ? (
                            <p className="text-center text-gray-600">Lade Firmenliste...</p>
                        ) : filteredFirmen.length === 0 ? (
                            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {searchTerm ? 'Keine passenden Firmen gefunden' : 'Keine Firmen registriert'}
                                </h3>
                            </div>
                        ) : (
                            <div className="firmen-grid">
                                {filteredFirmen.map((firma) => (
                                    <div key={firma.id} className="firma-card">
                                        <div className="firma-card-header">
                                            <h3 className="firma-name">
                                                {firma.firmenname}
                                            </h3>
                                            <span className={getStatusClass(firma.status)}>
                                                {firma.status || 'In Bearbeitung'}
                                            </span>
                                        </div>
                                        
                                        <div className="firma-card-body">
                                            <div className="firma-info">
                                                <p><strong>Adresse:</strong> {firma.adresse}</p>
                                                <p><strong>Kontaktperson:</strong> {firma.kontaktperson}</p>
                                                <p><strong>Email:</strong> {firma.email}</p>
                                                <p><strong>Telefon:</strong> {firma.telefon}</p>
                                            </div>
                                        </div>

                                        <div className="firma-card-footer">
                                            <button
                                                onClick={() => handleEditClick(firma)}
                                                className="btn btn-primary btn-sm"
                                            >
                                                Bearbeiten
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Modal verwenden */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onSubmit={handleUpdateFirma}
                loading={loading}
                password={password}
                onPasswordChange={handlePasswordChange}
            />
        </div>
    );
}

export default FirmenList;